import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import CrisisResources from '../components/CrisisResources';
import ProblemContactChat from '../components/ProblemContactChat';
import SEO from '../components/SEO';
import { useAuth } from '../context/AuthContext';
import { defaultProblemLabels, defaultProblemStatuses, problemDraftStorageKey } from '../data/problems';
import { visibilityOptions } from '../data/widgets';
import { ensureDefaultPrivacyGroups, listPrivacyGroups } from '../services/categoryService';
import {
  acceptProblemContactRequest,
  cancelProblemContactRequest,
  createProblemDoc,
  declineProblemContactRequest,
  listAcceptedProblemContactRequests,
  listBlockedUsers,
  listIncomingProblemContactRequests,
  listOutgoingProblemContactRequests,
  unblockUser,
  updateProblemDoc,
} from '../services/contentService';
import { ensureUserProfile } from '../services/profileService';

const emptyProblem = {
  id: '',
  title: '',
  summary: '',
  problem: '',
  solution: '',
  keyPoints: '',
  labels: ['other'],
  statuses: ['unknowns'],
  topic: 'other',
  status: 'unknowns',
  visibility: 'public',
  visibilityGroupId: '',
  anonymous: true,
  sourceType: '',
  sourceId: '',
  sourceTitle: '',
  sourceText: '',
};

function problemDraftFromStorage() {
  try {
    const draft = JSON.parse(localStorage.getItem(problemDraftStorageKey) || 'null');
    localStorage.removeItem(problemDraftStorageKey);
    return draft ? {
      ...emptyProblem,
      ...draft,
      labels: Array.isArray(draft.labels) && draft.labels.length ? draft.labels : emptyProblem.labels,
      statuses: Array.isArray(draft.statuses) && draft.statuses.length ? draft.statuses : emptyProblem.statuses,
    } : null;
  } catch (_err) {
    return null;
  }
}

function chipValue(value) {
  return String(value || '').trim().toLowerCase().replace(/\s+/g, ' ');
}

function Problems() {
  const { currentUser } = useAuth();
  const [initialProblemDraft] = useState(() => problemDraftFromStorage());
  const [form, setForm] = useState(() => initialProblemDraft || emptyProblem);
  const [showForm, setShowForm] = useState(() => Boolean(initialProblemDraft));
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [blockedUsers, setBlockedUsers] = useState([]);
  const [incomingContacts, setIncomingContacts] = useState([]);
  const [outgoingContacts, setOutgoingContacts] = useState([]);
  const [acceptedContacts, setAcceptedContacts] = useState([]);
  const [privacyGroups, setPrivacyGroups] = useState([]);
  const [customLabel, setCustomLabel] = useState('');
  const [customStatus, setCustomStatus] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const loadProblems = useCallback(async () => {
    if (!currentUser) return;
    setLoading(true);
    setError('');
    try {
      const [blocked, incoming, outgoing, accepted] = await Promise.all([
        listBlockedUsers(currentUser.uid),
        listIncomingProblemContactRequests(currentUser.uid),
        listOutgoingProblemContactRequests(currentUser.uid),
        listAcceptedProblemContactRequests(currentUser.uid),
      ]);
      await ensureDefaultPrivacyGroups(currentUser.uid);
      setPrivacyGroups(await listPrivacyGroups(currentUser.uid));
      setBlockedUsers(blocked);
      setIncomingContacts(incoming);
      setOutgoingContacts(outgoing);
      setAcceptedContacts(accepted);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    loadProblems();
  }, [loadProblems]);

  function updateForm(field, value) {
    setForm((current) => ({ ...current, [field]: value }));
  }

  function openNewProblem() {
    setForm(emptyProblem);
    setCustomLabel('');
    setCustomStatus('');
    setShowForm(true);
  }

  function closeProblemModal() {
    setForm(emptyProblem);
    setShowForm(false);
  }

  function toggleChip(field, value) {
    const normalized = chipValue(value);
    if (!normalized) return;
    setForm((current) => {
      const currentValues = Array.isArray(current[field]) ? current[field] : [];
      const exists = currentValues.includes(normalized);
      const nextValues = exists ? currentValues.filter((item) => item !== normalized) : [...currentValues, normalized];
      return { ...current, [field]: nextValues.length ? nextValues : [normalized] };
    });
  }

  function addCustomChip(field, value, reset) {
    const normalized = chipValue(value);
    if (!normalized) return;
    setForm((current) => ({
      ...current,
      [field]: [...new Set([...(Array.isArray(current[field]) ? current[field] : []), normalized])],
    }));
    reset('');
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setSaving(true);
    setMessage('');
    setError('');
    try {
      if (!form.title.trim() || !form.problem.trim()) {
        throw new Error('Title and problem are required.');
      }
      const profile = await ensureUserProfile(currentUser);
      if (form.id) {
        await updateProblemDoc(form.id, form, profile);
        setMessage('Problem document updated.');
      } else {
        await createProblemDoc(currentUser.uid, {
          ...form,
          topic: form.labels[0] || 'other',
          status: form.statuses[0] || 'unknowns',
        }, profile);
        setMessage('Problem opened.');
      }
      setForm(emptyProblem);
      setShowForm(false);
      await loadProblems();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleUnblock(ownerId) {
    setSaving(true);
    setMessage('');
    setError('');
    try {
      await unblockUser(currentUser.uid, ownerId);
      setBlockedUsers((current) => current.filter((uid) => uid !== ownerId));
      setMessage('Contributor unhidden.');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleAcceptContact(request) {
    setSaving(true);
    setMessage('');
    setError('');
    try {
      await acceptProblemContactRequest(request);
      setMessage('Problem contact accepted. Keep sharing carefully and use Friends if you want broader access.');
      await loadProblems();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleDeclineContact(requestId) {
    setSaving(true);
    setMessage('');
    setError('');
    try {
      await declineProblemContactRequest(requestId);
      setMessage('Problem contact declined.');
      await loadProblems();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleCancelContact(requestId) {
    setSaving(true);
    setMessage('');
    setError('');
    try {
      await cancelProblemContactRequest(requestId);
      setMessage('Problem contact request cancelled.');
      await loadProblems();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  function contactName(request) {
    return request.fromUid === currentUser?.uid
      ? request.toDisplayName
      : request.fromDisplayName;
  }

  const labelOptions = [...new Set([...defaultProblemLabels, ...(form.labels || [])])];
  const statusOptions = [...new Set([...defaultProblemStatuses, ...(form.statuses || [])])];

  return (
    <>
      <SEO title="Problems" />
      <section className="page-heading">
        <p className="eyebrow">Problems</p>
        <h1>Find help, share what worked, and talk with real people.</h1>
        <p>Problems is peer support and shared experience, not medical treatment. Choose anonymity, open a solution document, and discuss public or shared problem docs.</p>
      </section>
      {error && <p className="error-text">{error}</p>}
      {message && <p className="notice">{message}</p>}
      <section className="surface-card problems-intro">
        <div>
          <p className="tag">Opt in</p>
          <h2>Join problem discussions</h2>
          <p className="muted">Create a problem with what you know now. Solutions and key points can come later when the unknowns become clearer.</p>
        </div>
        <div className="button-row compact-row no-top-margin">
          <button className="button" type="button" onClick={openNewProblem}>Open a new problem</button>
          <Link className="button button-secondary" to="/dashboard/problems/search">Search problems</Link>
        </div>
      </section>
      <section className="surface-card safety-card">
        <p className="tag">Safety first</p>
        <h2>Problems is peer support, not emergency care.</h2>
        <p className="muted">If you might hurt yourself or someone else, contact local emergency services or a crisis hotline now. For health, legal, financial, or safety decisions, use this space as shared experience and talk to a qualified professional.</p>
        <div className="safety-list">
          <span>Report harmful or abusive posts</span>
          <span>Hide contributors from your view</span>
          <span>Chat only after accepted contact requests</span>
        </div>
      </section>
      <CrisisResources />
      <section className="surface-card problem-contact-panel">
        <div className="section-toolbar">
          <div>
            <p className="tag">Problem contacts</p>
            <h2>Connect only after consent.</h2>
            <p className="muted">Ask to connect from a problem document. The other person can accept or decline before any deeper conversation.</p>
          </div>
        </div>
        <div className="problem-contact-grid">
          {loading ? <p className="muted">Loading problem contacts...</p> : null}
          <div>
            <h3>Incoming</h3>
            {incomingContacts.length === 0 ? <p className="muted">No incoming contact requests.</p> : null}
            {incomingContacts.map((request) => (
              <article className="mini-card" key={request.id}>
                <strong>{request.fromDisplayName || request.fromUsername || 'Community member'}</strong>
                <span>{request.topic} · {request.problemTitle}</span>
                <p>{request.message}</p>
                <div className="button-row compact-row">
                  <button className="button button-secondary button-small" type="button" disabled={saving} onClick={() => handleAcceptContact(request)}>Accept</button>
                  <button className="button button-secondary button-small" type="button" disabled={saving} onClick={() => handleDeclineContact(request.id)}>Decline</button>
                </div>
              </article>
            ))}
          </div>
          <div>
            <h3>Sent</h3>
            {outgoingContacts.length === 0 ? <p className="muted">No pending sent contact requests.</p> : null}
            {outgoingContacts.map((request) => (
              <article className="mini-card" key={request.id}>
                <strong>{request.toDisplayName || 'Problem contributor'}</strong>
                <span>{request.topic} · {request.problemTitle}</span>
                <p>{request.message}</p>
                <button className="button button-secondary button-small" type="button" disabled={saving} onClick={() => handleCancelContact(request.id)}>Cancel</button>
              </article>
            ))}
          </div>
          <div>
            <h3>Accepted</h3>
            {acceptedContacts.length === 0 ? <p className="muted">No accepted problem contacts yet.</p> : null}
            {acceptedContacts.map((request) => (
              <article className="mini-card accepted-contact-card" key={request.id}>
                <strong>{contactName(request)}</strong>
                <span>{request.topic} · {request.problemTitle}</span>
                <p>Accepted. Keep personal details private unless both people deliberately choose otherwise.</p>
                <ProblemContactChat request={request} />
              </article>
            ))}
          </div>
        </div>
      </section>
      {blockedUsers.length ? (
        <section className="surface-card blocked-users-panel">
          <p className="tag">Hidden contributors</p>
          {blockedUsers.map((uid) => (
            <button className="button button-secondary button-small" type="button" disabled={saving} key={uid} onClick={() => handleUnblock(uid)}>Unhide {uid}</button>
          ))}
        </section>
      ) : null}
      {showForm ? (
        <div className="modal-backdrop" role="presentation">
          <form className="modal-card stacked-form" onSubmit={handleSubmit}>
            <div className="section-toolbar">
              <div>
                <p className="tag">Problem</p>
                <h2>{form.id ? 'Edit problem' : 'Open a new problem'}</h2>
                <p className="muted">Only title and problem are required. Labels and statuses help people find it.</p>
              </div>
              <button className="button button-secondary button-small" type="button" onClick={closeProblemModal}>Close</button>
            </div>
            {form.sourceText ? (
              <div className="inline-panel">
                <p className="tag">Started from {form.sourceType || 'source'}</p>
                <strong>{form.sourceTitle}</strong>
                <p className="muted">{form.sourceText}</p>
              </div>
            ) : null}
            <label>Title<input value={form.title} onChange={(event) => updateForm('title', event.target.value)} required /></label>
            <label>Problem<textarea rows="7" value={form.problem} onChange={(event) => updateForm('problem', event.target.value)} required /></label>
            <div className="chip-editor">
              <p className="tag">Labels</p>
              <div className="chip-row">
                {labelOptions.map((label) => (
                  <button className={`chip-button${form.labels.includes(label) ? ' active' : ''}`} type="button" key={label} onClick={() => toggleChip('labels', label)}>{label}</button>
                ))}
              </div>
              <div className="button-row compact-row no-top-margin">
                <input value={customLabel} onChange={(event) => setCustomLabel(event.target.value)} placeholder="Add custom label" />
                <button className="button button-secondary button-small" type="button" onClick={() => addCustomChip('labels', customLabel, setCustomLabel)}>Add label</button>
              </div>
            </div>
            <div className="chip-editor">
              <p className="tag">Status</p>
              <div className="chip-row">
                {statusOptions.map((status) => (
                  <button className={`chip-button${form.statuses.includes(status) ? ' active' : ''}`} type="button" key={status} onClick={() => toggleChip('statuses', status)}>{status}</button>
                ))}
              </div>
              <div className="button-row compact-row no-top-margin">
                <input value={customStatus} onChange={(event) => setCustomStatus(event.target.value)} placeholder="Add custom status" />
                <button className="button button-secondary button-small" type="button" onClick={() => addCustomChip('statuses', customStatus, setCustomStatus)}>Add status</button>
              </div>
            </div>
            <label>Visibility
              <select value={form.visibility} onChange={(event) => updateForm('visibility', event.target.value)}>
                {visibilityOptions.map((option) => <option value={option.value} key={option.value}>{option.label}</option>)}
              </select>
            </label>
            {form.visibility === 'group' ? (
              <label>Group
                <select value={form.visibilityGroupId || ''} onChange={(event) => updateForm('visibilityGroupId', event.target.value)} required>
                  <option value="">Select group</option>
                  {privacyGroups.map((group) => <option value={group.id} key={group.id}>{group.name}</option>)}
                </select>
              </label>
            ) : null}
            <label className="checkbox-label"><input type="checkbox" checked={form.anonymous} onChange={(event) => updateForm('anonymous', event.target.checked)} /> Publish anonymously</label>
            <div className="button-row compact-row">
              <button className="button" type="submit" disabled={saving}>{saving ? 'Saving...' : 'Save problem'}</button>
              <button className="button button-secondary" type="button" onClick={closeProblemModal}>Cancel</button>
            </div>
          </form>
        </div>
      ) : null}
    </>
  );
}

export default Problems;
